/// <reference types="@types/google.maps" />
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Wrapper, Status } from '@googlemaps/react-wrapper';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MapPin, Search, Crosshair } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';

interface MapLocationPickerProps {
  latitude?: number;
  longitude?: number;
  address?: string;
  onLocationChange: (location: { latitude: number; longitude: number; address: string }) => void;
}

interface MapComponentProps {
  center: google.maps.LatLngLiteral;
  zoom: number;
  onLocationSelect: (location: { latitude: number; longitude: number; address: string }) => void;
  selectedLocation?: google.maps.LatLngLiteral;
  onError?: (error: string | null) => void;
}

const MapComponent: React.FC<MapComponentProps> = ({ 
  center, 
  zoom, 
  onLocationSelect, 
  selectedLocation,
  onError
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map>();
  const [marker, setMarker] = useState<google.maps.Marker>();
  const [geocoder, setGeocoder] = useState<google.maps.Geocoder>();
  const [mapError, setMapError] = useState<string | null>(null);

  useEffect(() => {
    if (ref.current && !map && window.google && window.google.maps) {
      try {
        const newMap = new window.google.maps.Map(ref.current, {
          center,
          zoom,
          mapTypeControl: true,
          streetViewControl: true,
          fullscreenControl: true,
        });
        setMap(newMap);
        setGeocoder(new window.google.maps.Geocoder());
        setMapError(null);
        onError?.(null);
      } catch (error) {
        console.error('Error initializing Google Maps:', error);
        const errorMessage = 'Failed to initialize Google Maps. Please check your API key.';
        setMapError(errorMessage);
        onError?.(errorMessage);
      }
    }
  }, [ref, map, center, zoom, onError]);

  useEffect(() => {
    if (map && selectedLocation && !mapError) {
      try {
        // Update map center and marker when selectedLocation changes
        map.setCenter(selectedLocation);
        
        if (marker) {
          marker.setPosition(selectedLocation);
        } else {
          const newMarker = new window.google.maps.Marker({
            position: selectedLocation,
            map: map,
            draggable: true,
            title: 'Property Location',
          });
          
          newMarker.addListener('dragend', () => {
            const position = newMarker.getPosition();
            if (position && geocoder) {
              const lat = position.lat();
              const lng = position.lng();
              
              geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                if (status === 'OK' && results?.[0]) {
                  onLocationSelect({
                    latitude: lat,
                    longitude: lng,
                    address: results[0].formatted_address,
                  });
                } else {
                  onLocationSelect({
                    latitude: lat,
                    longitude: lng,
                    address: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
                  });
                }
              });
            }
          });
          
          setMarker(newMarker);
        }
      } catch (error) {
        console.error('Error updating marker:', error);
        const errorMessage = 'Error updating map marker';
        setMapError(errorMessage);
        onError?.(errorMessage);
      }
    }
  }, [map, selectedLocation, marker, geocoder, onLocationSelect, mapError, onError]);

  useEffect(() => {
    if (map) {
      const clickListener = map.addListener('click', (event: google.maps.MapMouseEvent) => {
        const lat = event.latLng?.lat();
        const lng = event.latLng?.lng();
        
        if (lat !== undefined && lng !== undefined && geocoder) {
          // Update marker position
          if (marker) {
            marker.setPosition({ lat, lng });
          } else {
            const newMarker = new window.google.maps.Marker({
              position: { lat, lng },
              map: map,
              draggable: true,
              title: 'Property Location',
            });
            
            newMarker.addListener('dragend', () => {
              const position = newMarker.getPosition();
              if (position && geocoder) {
                const dragLat = position.lat();
                const dragLng = position.lng();
                
                geocoder.geocode({ location: { lat: dragLat, lng: dragLng } }, (results, status) => {
                  if (status === 'OK' && results?.[0]) {
                    onLocationSelect({
                      latitude: dragLat,
                      longitude: dragLng,
                      address: results[0].formatted_address,
                    });
                  } else {
                    onLocationSelect({
                      latitude: dragLat,
                      longitude: dragLng,
                      address: `${dragLat.toFixed(6)}, ${dragLng.toFixed(6)}`,
                    });
                  }
                });
              }
            });
            
            setMarker(newMarker);
          }
          
          // Reverse geocode to get address
          geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === 'OK' && results?.[0]) {
              onLocationSelect({
                latitude: lat,
                longitude: lng,
                address: results[0].formatted_address,
              });
            } else {
              onLocationSelect({
                latitude: lat,
                longitude: lng,
                address: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
              });
            }
          });
        }
      });

      return () => {
        google.maps.event.removeListener(clickListener);
      };
    }
  }, [map, marker, geocoder, onLocationSelect]);

  return <div ref={ref} style={{ width: '100%', height: '400px' }} />;
};

const render = (status: Status) => {
  switch (status) {
    case Status.LOADING:
      return <div className="flex items-center justify-center h-96">Loading Google Maps...</div>;
    case Status.FAILURE:
      return (
        <Alert variant="destructive">
          <AlertDescription>
            <div className="space-y-2">
              <p className="font-semibold">Failed to load Google Maps</p>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Please check your API key configuration in .env file</li>
                <li>Verify that VITE_GOOGLE_MAPS_API_KEY is set correctly</li>
                <li>Ensure the Maps JavaScript API is enabled in Google Cloud Console</li>
                <li>Check API key restrictions and billing settings</li>
              </ul>
              <p className="text-xs mt-2">
                Error: InvalidKeyMapError - The provided API key is invalid or missing required permissions.
              </p>
            </div>
          </AlertDescription>
        </Alert>
      );
    case Status.SUCCESS:
      return <></>;
  }
};

export const MapLocationPicker: React.FC<MapLocationPickerProps> = ({
  latitude = 24.7136, // Default to Riyadh, Saudi Arabia
  longitude = 46.6753,
  address = '',
  onLocationChange,
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [currentLocation, setCurrentLocation] = useState({
    latitude,
    longitude,
    address,
  });
  const [isSearching, setIsSearching] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);

  const handleLocationSelect = useCallback((location: { latitude: number; longitude: number; address: string }) => {
    setCurrentLocation(location);
    onLocationChange(location);
  }, [onLocationChange]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    // Check if Google Maps is loaded
    if (!window.google || !window.google.maps || !window.google.maps.Geocoder) {
      console.error('Google Maps API is not loaded yet');
      toast({
        title: 'Error',
        description: 'Google Maps is still loading. Please wait a moment and try again.',
        variant: 'destructive',
      });
      return;
    }
    
    setIsSearching(true);
    setMapError(null);
    try {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: searchQuery }, (results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
        if (status === 'OK' && results?.[0]) {
          const location = results[0].geometry.location;
          const lat = location.lat();
          const lng = location.lng();
          
          handleLocationSelect({
            latitude: lat,
            longitude: lng,
            address: results[0].formatted_address,
          });
        } else {
          toast({
            title: 'Search failed',
            description: 'Could not find the location. Please try a different search term.',
            variant: 'destructive',
          });
        }
        setIsSearching(false);
      });
    } catch (error) {
      console.error('Search error:', error);
      toast({
        title: 'Error',
        description: 'An error occurred while searching. Please try again.',
        variant: 'destructive',
      });
      setIsSearching(false);
    }
  };

  const handleGetCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast({
        title: 'Geolocation not available',
        description: 'Your browser does not support geolocation.',
        variant: 'destructive',
      });
      return;
    }

    // Check if Google Maps is loaded
    if (!window.google || !window.google.maps || !window.google.maps.Geocoder) {
      console.error('Google Maps API is not loaded yet');
      toast({
        title: 'Error',
        description: 'Google Maps is still loading. Please wait a moment and try again.',
        variant: 'destructive',
      });
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        
        try {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === 'OK' && results?.[0]) {
              handleLocationSelect({
                latitude: lat,
                longitude: lng,
                address: results[0].formatted_address,
              });
            } else {
              handleLocationSelect({
                latitude: lat,
                longitude: lng,
                address: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
              });
            }
          });
        } catch (error) {
          console.error('Geocoding error:', error);
          handleLocationSelect({
            latitude: lat,
            longitude: lng,
            address: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
          });
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        toast({
          title: 'Location access denied',
          description: 'Please allow location access to use this feature.',
          variant: 'destructive',
        });
      }
    );
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Get Google Maps API key from environment variables
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';
  
  // Debug logging
  if (!apiKey) {
    console.warn('Google Maps API Key is missing. Please add VITE_GOOGLE_MAPS_API_KEY to your .env file');
  }

  if (!apiKey) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            {t('location')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Alert>
            <AlertDescription>
              Google Maps API key is not configured. Please add VITE_GOOGLE_MAPS_API_KEY to your environment variables.
            </AlertDescription>
          </Alert>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="latitude">{t('latitude')}</Label>
              <Input
                id="latitude"
                type="number"
                step="any"
                value={currentLocation.latitude}
                onChange={(e) => handleLocationSelect({
                  ...currentLocation,
                  latitude: parseFloat(e.target.value) || 0
                })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="longitude">{t('longitude')}</Label>
              <Input
                id="longitude"
                type="number"
                step="any"
                value={currentLocation.longitude}
                onChange={(e) => handleLocationSelect({
                  ...currentLocation,
                  longitude: parseFloat(e.target.value) || 0
                })}
                required
              />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MapPin className="h-5 w-5" />
          {t('location')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Search Bar */}
        <div className="flex gap-2">
          <div className="flex-1">
            <Input
              placeholder={t('searchLocation') || 'Search for a location...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleKeyPress}
            />
          </div>
          <Button 
            type="button" 
            onClick={handleSearch} 
            disabled={isSearching}
            size="icon"
          >
            <Search className="h-4 w-4" />
          </Button>
          <Button 
            type="button" 
            onClick={handleGetCurrentLocation}
            variant="outline"
            size="icon"
            title={t('useCurrentLocation') || 'Use current location'}
          >
            <Crosshair className="h-4 w-4" />
          </Button>
        </div>

        {/* Google Map */}
        <div className="border rounded-lg overflow-hidden">
          <Wrapper apiKey={apiKey} render={render}>
            <MapComponent
              center={{ lat: currentLocation.latitude, lng: currentLocation.longitude }}
              zoom={15}
              onLocationSelect={handleLocationSelect}
              selectedLocation={{ lat: currentLocation.latitude, lng: currentLocation.longitude }}
              onError={setMapError}
            />
          </Wrapper>
        </div>
        
        {mapError && (
          <Alert variant="destructive" className="mt-2">
            <AlertDescription>{mapError}</AlertDescription>
          </Alert>
        )}

        {/* Selected Location Info */}
        <div className="space-y-2">
          <Label>{t('selectedLocation') || 'Selected Location'}</Label>
          <div className="p-3 bg-muted rounded-lg">
            <p className="text-sm font-medium">{currentLocation.address || t('noAddressSelected') || 'No address selected'}</p>
            <p className="text-xs text-muted-foreground">
              {t('coordinates') || 'Coordinates'}: {currentLocation.latitude.toFixed(6)}, {currentLocation.longitude.toFixed(6)}
            </p>
          </div>
        </div>

        {/* Manual Coordinate Input (as fallback) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="manual-latitude">{t('latitude')}</Label>
            <Input
              id="manual-latitude"
              type="number"
              step="any"
              value={currentLocation.latitude}
              onChange={(e) => handleLocationSelect({
                ...currentLocation,
                latitude: parseFloat(e.target.value) || 0
              })}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="manual-longitude">{t('longitude')}</Label>
            <Input
              id="manual-longitude"
              type="number"
              step="any"
              value={currentLocation.longitude}
              onChange={(e) => handleLocationSelect({
                ...currentLocation,
                longitude: parseFloat(e.target.value) || 0
              })}
              required
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
